import { supabase } from '../lib/supabase';

export type DocumentType = 
  | 'student_id'
  | 'sponsor_id'
  | 'proof_of_residence'
  | 'university_registration'
  | 'sponsor_payslips'
  | 'sponsor_bank_statements';

export const getRequiredDocuments = (fundingType: string): DocumentType[] => {
  const baseDocuments: DocumentType[] = [
    'student_id',
    'proof_of_residence',
    'university_registration'
  ];

  if (fundingType !== 'nsfas') {
    return [
      ...baseDocuments,
      'sponsor_id',
      'sponsor_payslips',
      'sponsor_bank_statements'
    ];
  }

  return baseDocuments;
};

export const validateDocuments = (
  documents: Record<DocumentType, string | undefined>,
  fundingType: string
): { valid: boolean; missing: DocumentType[] } => {
  const requiredDocs = getRequiredDocuments(fundingType);
  const missing = requiredDocs.filter(doc => !documents[doc]);

  return {
    valid: missing.length === 0,
    missing
  };
};

export const uploadDocument = async (
  file: File,
  documentType: DocumentType,
  studentId: string,
  description?: string
): Promise<string> => {
  try {
    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error(`File size must be less than 5MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPG, PNG or PDF file');
    }

    // Generate unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${documentType}_${Date.now()}.${fileExt}`;
    const filePath = `${studentId}/${fileName}`;

    // Upload file to storage
    const { error: uploadError, data } = await supabase.storage
      .from('documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('documents')
      .getPublicUrl(filePath);

    // Create document record
    const { error: dbError } = await supabase
      .from('documents')
      .insert({
        student_id: studentId,
        document_type: documentType,
        file_url: publicUrl,
        description,
        uploaded_at: new Date().toISOString(),
        verified: false
      });

    if (dbError) throw dbError;

    return publicUrl;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to upload document');
  }
};

export const getUploadedDocuments = async (studentId: string) => {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('student_id', studentId);

  if (error) throw error;
  return data;
};

export const updateStudentStatus = async (studentId: string, status: 'pending' | 'approved' | 'rejected') => {
  const { error } = await supabase
    .from('students')
    .update({ application_status: status })
    .eq('id', studentId);

  if (error) throw error;
};